import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import DeleteLogo from '../../assets/images/x-icon.svg?react';
import '../RunTest/RunTest.css';
import {
  hasRecipientSeparator,
  parseRecipients,
  RECIPIENT_ERRORS,
  RECIPIENT_SEPARATOR_KEYS
} from '../../utils/recipientEmails';

interface EmailsBoxProps {
  items: string[];
  setItems: React.Dispatch<React.SetStateAction<string[]>>;
}

export interface EmailsBoxHandle {
  flush: () => string[] | null;
}

export const EmailsBox = forwardRef<EmailsBoxHandle, EmailsBoxProps>(({ items, setItems }, ref) => {
  const [value, setValue] = React.useState('');
  const [errors, setErrors] = React.useState<string[]>([]);

  // Kept in sync synchronously so flush() sees the result of a blur that happened in the same click
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const valueRef = useRef(value);
  valueRef.current = value;

  const commit = (text: string) => {
    const { added, remaining, errors } = parseRecipients(text, itemsRef.current);
    const nextItems = added.length ? [...itemsRef.current, ...added] : itemsRef.current;

    itemsRef.current = nextItems;
    valueRef.current = remaining;
    setItems(nextItems);
    setValue(remaining);
    setErrors(errors);

    return { remaining, errors };
  };

  useImperativeHandle(ref, () => ({
    flush: () => {
      const { remaining, errors } = commit(valueRef.current);

      if (remaining) {
        setErrors([...errors, RECIPIENT_ERRORS.submitBlocked]);
        return null;
      }

      return itemsRef.current;
    }
  }));

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (RECIPIENT_SEPARATOR_KEYS.includes(event.key)) {
      event.preventDefault();
      commit(value);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData.getData('text');

    if (!hasRecipientSeparator(pastedText)) return;

    event.preventDefault();
    const { selectionStart, selectionEnd } = event.currentTarget;
    const start = selectionStart ?? value.length;
    const end = selectionEnd ?? value.length;

    commit(`${value.slice(0, start)}${pastedText}${value.slice(end)}`);
  };

  const handleBlur = () => {
    if (value.trim()) {
      commit(value);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    setErrors([]);
  };

  const handleDelete = (item: string) => {
    setItems((items) => items.filter((i) => i !== item));
  };

  return (
    <div style={{ margin: '0 48px' }}>
      <div
        style={{
          border: '1px solid #979797',
          borderRadius: '6px',
          padding: '5px'
        }}
      >
        {items.map((item) => (
          <span className="tag-item" key={item} style={{ cursor: 'pointer' }}>
            {item}
            <DeleteLogo onClick={() => handleDelete(item)} />
          </span>
        ))}
        <input
          className="emailsBox-textarea"
          type={'text'}
          value={value}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </div>

      {errors.map((error) => (
        <p key={error} style={{ color: 'red' }}>
          {error}
        </p>
      ))}
    </div>
  );
});

EmailsBox.displayName = 'EmailsBox';
